import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { eq } from 'tio-ui/utilities';
import {
  PhCaretDown,
  PhCaretUp,
  PhCheck,
  PhClock,
  PhCurrencyCircleDollar,
  PhInfo,
  PhThumbsUp,
  PhWarningCircle,
  PhXCircle,
} from 'ember-phosphor-icons';
import Chip from 'tio-reimagined/components/chip';
import type { ChipIntent } from 'tio-reimagined/components/chip';
import safeFormatDate from '../../helpers/safe-format-date.ts';
import type { AppHistoryBase } from '../../types/tuition-assistance.ts';
import type IntlService from 'ember-intl/services/intl';
import { formatAsDollars } from '../../utils/format.ts';

type EventCategory = 'rejection' | 'approval' | 'submission' | 'info' | 'payment' | 'neutral';

interface TimelineEvent {
  category: EventCategory;
  label: string;
  date: string;
  approverName?: string;
  approverComment?: string;
  role?: string;
  chipLabel?: string;
  chipIntent?: ChipIntent;
  isLast: boolean;
}

interface ApplicationHistoryTimelineSignature {
  Args: {
    history: AppHistoryBase[];
    title?: string;
    translationPrefix?: string;
    /** When true, hides the header/border — for use inside a drawer or other container */
    embedded?: boolean;
  };
  Element: HTMLDivElement;
}

// Maps a state transition type to an event category
function categorizeStateTransition(type?: string): EventCategory {
  if (!type) return 'neutral';
  if (type.includes('NOT_APPROVED') || type.includes('REJECTED')) return 'rejection';
  if (type.includes('APPROVED') || type === 'FULFILLED') return 'approval';
  if (type.includes('PENDING') || type === 'COURSES_IN_PROGRESS') return 'submission';
  if (type === 'INFO_REQUESTED') return 'info';
  if (type === 'ABANDONED') return 'neutral';
  return 'neutral';
}

// Assistance types that represent actual money movement
const PAYMENT_ASSISTANCE_TYPES = new Set([
  'PAYMENT',
  'PAYMENT_PENDING',
  'PAYMENT_AMOUNT_UPDATED',
  'CARD_AUTHORIZED',
  'CARD_CAPTURED',
  'REPAYMENT_REQUEST',
  'REPAYMENT',
  'REPAYMENT_FORGIVEN',
  'REPAYMENT_CANCELLED',
  'REPAYMENT_AMOUNT_UPDATED',
]);

// Assistance types that are informational (card status, not actual payments)
const INFO_ASSISTANCE_TYPES = new Set([
  'CARD_RESERVATION_CREATED',
  'CARD_RESERVATION_AMOUNT_UPDATED',
  'CARD_EXPIRED',
  'CARD_REFUNDED',
  'CARD_DECLINED',
  'CARD_DISPUTED',
]);

function categorizeAssistance(assistanceType?: string): EventCategory {
  if (!assistanceType) return 'neutral';
  if (PAYMENT_ASSISTANCE_TYPES.has(assistanceType)) return 'payment';
  if (INFO_ASSISTANCE_TYPES.has(assistanceType)) return 'info';
  return 'neutral';
}

// Filter out internal system strings that shouldn't be shown to users
function sanitizeComment(comment?: string): string | undefined {
  if (!comment) return undefined;
  if (comment.includes('.state_') || comment.includes('::')) {
    return undefined;
  }
  // Filter Ruby class-name strings (e.g. "TasAssistance::Foo") but not
  // legitimate comments that happen to start with a proper noun.
  if (/^[A-Z][a-z]+[A-Z]/.test(comment) && !/\s/.test(comment)) {
    return undefined;
  }
  return comment;
}

function chipForCategory(
  category: EventCategory,
  stateTransitionType?: string
): { label: string; intent: ChipIntent } | null {
  switch (category) {
    case 'rejection':
      return { label: 'Action Needed', intent: 'danger' };
    case 'approval':
      return { label: 'Approved', intent: 'success' };
    case 'submission':
      if (stateTransitionType === 'COURSES_IN_PROGRESS') {
        return { label: 'In Progress', intent: 'primary' };
      }
      return { label: 'Submitted', intent: 'primary' };
    case 'info':
      return { label: 'Update', intent: 'warning' };
    case 'payment':
      return { label: 'Payment', intent: 'primary' };
    default:
      return null;
  }
}

// Category → Tailwind color classes for the timeline dot
const DOT_CLASSES: Record<EventCategory, string> = {
  rejection: 'bg-red-500 ring-red-100 dark:ring-red-900/40',
  approval: 'bg-emerald-500 ring-emerald-100 dark:ring-emerald-900/40',
  submission: 'bg-[#006c9a] ring-[#006c9a]/10 dark:ring-[#006c9a]/30',
  info: 'bg-amber-500 ring-amber-100 dark:ring-amber-900/40',
  payment: 'bg-[#006c9a] ring-[#006c9a]/10 dark:ring-[#006c9a]/30',
  neutral: 'bg-gray-400 ring-gray-100 dark:ring-gray-700',
};

// Category → Tailwind color for the left border accent on comment cards
const COMMENT_BORDER_CLASSES: Record<EventCategory, string> = {
  rejection: 'border-l-red-400',
  approval: 'border-l-emerald-400',
  submission: 'border-l-[#006c9a]',
  info: 'border-l-amber-400',
  payment: 'border-l-[#006c9a]',
  neutral: 'border-l-gray-300',
};

class ApplicationHistoryTimeline extends Component<ApplicationHistoryTimelineSignature> {
  @service declare intl: IntlService;
  @tracked isExpanded = true;

  get prefix() {
    return this.args.translationPrefix || 'tuition_assistance.applications.history';
  }

  toggle = () => {
    this.isExpanded = !this.isExpanded;
  };

  get timelineEvents(): TimelineEvent[] {
    return this.args.history
      .filter(
        (item) =>
          item.logType !== 'state_transition' ||
          (item as { stateTransitionType?: string }).stateTransitionType !== 'CREATED'
      )
      .map((item): Omit<TimelineEvent, 'isLast'> => {
        const formattedName = item.approverName
          ? ' by ' + item.approverName + (item.role ? ` - ${item.role}` : '')
          : '';

        let category: EventCategory;
        let label: string;

        switch (item.logType) {
          case 'assistance': {
            category = categorizeAssistance(item.assistanceType);
            label = this.intl.t(`${this.prefix}.${item.assistanceType}`, {
              amount: formatAsDollars(Number(item.amount) || 0),
              name: item.approverName ? ' by ' + item.approverName : '',
            });
            break;
          }
          case 'approval': {
            category = 'approval';
            const updateType =
              item.from === item.to ? 'approved_total_set' : 'approved_total_updated';
            label = this.intl.t(`${this.prefix}.${updateType}`, {
              amount: formatAsDollars(Number(item.to) || 0),
              from: formatAsDollars(Number(item.from) || 0),
              to: formatAsDollars(Number(item.to) || 0),
              name: item.approverName ? ' by ' + item.approverName : '',
            });
            break;
          }
          case 'state_transition':
          default: {
            category = categorizeStateTransition(item.stateTransitionType);
            label = this.intl.t(`${this.prefix}.${item.stateTransitionType}`, {
              name: formattedName,
            });
            break;
          }
        }

        const chip = chipForCategory(
          category,
          item.logType === 'state_transition' ? item.stateTransitionType : undefined
        );

        return {
          category,
          label,
          date: item.date,
          approverName: item.approverName || 'System',
          approverComment: sanitizeComment(item.approverComment),
          role: item.role,
          chipLabel: chip?.label,
          chipIntent: chip?.intent,
        };
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map((event, idx, arr) => ({ ...event, isLast: idx === arr.length - 1 }));
  }

  <template>
    <div
      class={{unless
        @embedded
        "rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
      }}
      ...attributes
    >
      {{#unless @embedded}}
        {{! Header — only shown in standalone mode }}
        <button
          type="button"
          class="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-xl transition-colors"
          {{on "click" this.toggle}}
        >
          <div class="flex items-center gap-2.5">
            <PhClock @size={{18}} class="text-gray-400" />
            <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {{if @title @title (t "tuition_assistance.history.title")}}
            </span>
            <span
              class="inline-flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300"
            >
              {{this.timelineEvents.length}}
            </span>
          </div>
          {{#if this.isExpanded}}
            <PhCaretUp @size={{16}} class="text-gray-400" />
          {{else}}
            <PhCaretDown @size={{16}} class="text-gray-400" />
          {{/if}}
        </button>
      {{/unless}}

      {{! Timeline — always visible in embedded mode }}
      {{#if (if @embedded true this.isExpanded)}}
        <div class={{unless @embedded "px-5 pb-5"}}>
          <div class="relative">
            {{#each this.timelineEvents as |event|}}
              <div class="relative flex gap-4 {{unless event.isLast 'pb-6'}}">
                {{! Vertical connecting line (not on last item) }}
                {{#unless event.isLast}}
                  <div
                    class="absolute left-3 top-6 bottom-0 w-px bg-gray-200 dark:bg-gray-700"
                  ></div>
                {{/unless}}

                {{! Timeline dot }}
                <div class="relative z-10 shrink-0 mt-0.5">
                  <div
                    class="flex h-6 w-6 items-center justify-center rounded-full ring-4
                      {{this.dotClass event.category}}"
                  >
                    {{#if (eq event.category "rejection")}}
                      <PhXCircle @size={{14}} class="text-white" />
                    {{else if (eq event.category "approval")}}
                      <PhCheck @size={{14}} class="text-white" />
                    {{else if (eq event.category "submission")}}
                      <PhThumbsUp @size={{14}} class="text-white" />
                    {{else if (eq event.category "info")}}
                      <PhInfo @size={{14}} class="text-white" />
                    {{else if (eq event.category "payment")}}
                      <PhCurrencyCircleDollar @size={{14}} class="text-white" />
                    {{else}}
                      <PhWarningCircle @size={{14}} class="text-white" />
                    {{/if}}
                  </div>
                </div>

                {{! Event content }}
                <div class="flex-1 min-w-0 pt-0.5">
                  <div class="flex items-start justify-between gap-x-3">
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-0">
                      {{event.label}}
                    </p>
                    <div class="flex shrink-0 flex-col items-end gap-1">
                      <time datetime={{event.date}} class="text-xs text-right whitespace-nowrap">
                        <span class="text-gray-400 dark:text-gray-500">
                          {{safeFormatDate event.date month="short" day="2-digit" year="numeric"}}
                        </span>
                        <span class="px-1 text-gray-300 dark:text-gray-600">|</span>
                        <span class="font-medium text-gray-600 dark:text-gray-300">
                          {{safeFormatDate
                            event.date
                            hour="numeric"
                            minute="2-digit"
                            hour12=true
                            timeZoneName="short"
                          }}
                        </span>
                      </time>
                      {{#if event.chipLabel}}
                        <Chip @intent={{event.chipIntent}} @size="sm" @appearance="faded">
                          {{event.chipLabel}}
                        </Chip>
                      {{/if}}
                    </div>
                  </div>

                  {{! Approver comment card }}
                  {{#if event.approverComment}}
                    <div
                      class="mt-2 rounded-lg border border-l-4 border-gray-200 dark:border-gray-700
                        {{this.commentBorderClass event.category}}
                        bg-gray-50 dark:bg-gray-800/50 p-3"
                    >
                      <div class="flex items-center gap-1.5 mb-1">
                        <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {{event.approverName}}
                        </span>
                        {{#if event.role}}
                          <span
                            class="text-xs text-gray-400 before:content-['·'] before:mr-1.5"
                          >{{event.role}}</span>
                        {{/if}}
                      </div>
                      <p class="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        {{event.approverComment}}
                      </p>
                    </div>
                  {{/if}}
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>

  dotClass = (category: EventCategory) => DOT_CLASSES[category] ?? DOT_CLASSES.neutral;
  commentBorderClass = (category: EventCategory) =>
    COMMENT_BORDER_CLASSES[category] ?? COMMENT_BORDER_CLASSES.neutral;
}

export { type ApplicationHistoryTimelineSignature };
export default ApplicationHistoryTimeline;
